import React, { useEffect, useState } from 'react';
import { SaveIcon, GlobeIcon, ClockIcon, InfoIcon, UploadCloudIcon } from 'lucide-react';
export function CurrencyDetail({
  currency,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    currencyId: '',
    code: '',
    numericCode: '',
    name: '',
    symbol: '',
    countries: [],
    isDefault: false,
    decimalPlaces: 2
  });
  // Sample history data - in real app, this would come from API
  const [history, setHistory] = useState([{
    id: 1,
    eventType: 'updated',
    eventDescription: 'Currency symbol changed from "$" to "A$"',
    timestamp: '2026-01-19T20:02:52Z',
    userId: 'user-123',
    userName: 'John Smith',
    changes: [{
      field: 'symbol',
      oldValue: '$',
      newValue: 'A$'
    }]
  }, {
    id: 2,
    eventType: 'created',
    eventDescription: 'Currency created',
    timestamp: '2026-01-14T22:25:08Z',
    userId: 'user-456',
    userName: 'Jane Doe',
    changes: []
  }]);
  // Common country options for dropdown
  const commonCountries = ['United States', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Japan', 'China', 'India', 'Canada', 'Australia', 'Brazil', 'Russia', 'South Africa', 'Mexico', 'Singapore'];
  useEffect(() => {
    if (currency && !isCreating) {
      setFormData({
        ...currency,
        countries: [...currency.countries]
      });
    } else {
      setFormData({
        currencyId: '',
        code: '',
        numericCode: '',
        name: '',
        symbol: '',
        countries: [],
        isDefault: false,
        decimalPlaces: 2
      });
    }
  }, [currency, isCreating]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleNumericChange = e => {
    const {
      name,
      value
    } = e.target;
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else if (value === '') {
      setFormData({
        ...formData,
        [name]: ''
      });
    }
  };
  const handleCountrySelect = e => {
    const selectedCountry = e.target.value;
    if (selectedCountry && !formData.countries.includes(selectedCountry)) {
      setFormData({
        ...formData,
        countries: [...formData.countries, selectedCountry]
      });
    }
  };
  const handleRemoveCountry = country => {
    setFormData({
      ...formData,
      countries: formData.countries.filter(c => c !== country)
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  const getEventIcon = eventType => {
    switch (eventType) {
      case 'created':
        return <UploadCloudIcon className="h-5 w-5" data-id="element-17011" />;
      case 'updated':
        return <InfoIcon className="h-5 w-5" data-id="element-17012" />;
      default:
        return <InfoIcon className="h-5 w-5" data-id="element-17013" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17014">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-17015">
        <div data-id="element-17016">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17017">
            {isCreating ? 'Add New Currency' : `${formData.name} (${formData.code})`}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17018">
            {isCreating ? 'Enter details for the new currency' : 'View and update currency details'}
          </p>
        </div>
        {activeTab === 'details' && <div className="flex space-x-3" data-id="element-17019">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17020">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17021">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-17022" />
              Save Currency
            </button>
          </div>}
      </div>

      {/* Tabs */}
      {!isCreating && <div className="border-b border-gray-200" data-id="element-17023">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-17024">
            <button onClick={() => setActiveTab('details')} className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17025">
              Details
            </button>
            <button onClick={() => setActiveTab('relationships')} className={`${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17026">
              Relationships
            </button>
            <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17027">
              History
            </button>
          </nav>
        </div>}

      {/* Tab Content */}
      <div className="px-4 py-5 sm:px-6" data-id="element-17028">
        {/* Details Tab */}
        {activeTab === 'details' && <form className="space-y-6" onSubmit={handleSubmit} data-id="element-17029">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17030">
              {/* Basic Information */}
              <div className="sm:col-span-6" data-id="element-17031">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-17032">
                  Basic Information
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-17033">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-17034">
                  Currency Name
                </label>
                <div className="mt-1" data-id="element-17035">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17036" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-17037">
                <label htmlFor="symbol" className="block text-sm font-medium text-gray-700" data-id="element-17038">
                  Symbol
                </label>
                <div className="mt-1" data-id="element-17039">
                  <input type="text" name="symbol" id="symbol" value={formData.symbol} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17040" />
                </div>
              </div>
              {/* ISO Codes */}
              <div className="sm:col-span-6" data-id="element-17041">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-17042">
                  ISO Codes
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-17043">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-17044">
                  Currency Code (ISO 4217)
                </label>
                <div className="mt-1" data-id="element-17045">
                  <input type="text" name="code" id="code" value={formData.code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} data-id="element-17046" />
                </div>
                {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-17047">
                    Currency code cannot be changed after creation
                  </p>}
              </div>
              <div className="sm:col-span-3" data-id="element-17048">
                <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-17049">
                  Numeric Code (ISO 4217)
                </label>
                <div className="mt-1" data-id="element-17050">
                  <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17051" />
                </div>
              </div>
              {/* Formatting */}
              <div className="sm:col-span-6" data-id="element-17052">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-17053">
                  Formatting
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-17054">
                <div className="flex items-center h-full pt-6" data-id="element-17055">
                  <input id="isDefault" name="isDefault" type="checkbox" checked={formData.isDefault} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-17056" />
                  <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700" data-id="element-17057">
                    Set as default currency
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 pl-6" data-id="element-17058">
                  {formData.isDefault ? 'This will be the default currency for new transactions' : 'Check to make this the default currency'}
                </p>
              </div>
              {/* Countries */}
              <div className="sm:col-span-6" data-id="element-17059">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-17060">
                  Countries Using This Currency
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-17061">
                <label htmlFor="countrySelect" className="block text-sm font-medium text-gray-700" data-id="element-17062">
                  Add Country
                </label>
                <div className="mt-1" data-id="element-17063">
                  <select id="countrySelect" name="countrySelect" value="" onChange={handleCountrySelect} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17064">
                    <option value="" data-id="element-17065">Select a country</option>
                    {commonCountries.map(country => <option key={country} value={country} data-id="element-17066">
                        {country}
                      </option>)}
                    <option value="Other" data-id="element-17067">Other (type manually)</option>
                  </select>
                </div>
              </div>
              {/* Custom country input */}
              {formData.countries.includes('Other') && <div className="sm:col-span-3" data-id="element-17068">
                  <label htmlFor="customCountry" className="block text-sm font-medium text-gray-700" data-id="element-17069">
                    Custom Country Name
                  </label>
                  <div className="mt-1 flex" data-id="element-17070">
                    <input type="text" id="customCountry" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter country name" data-id="element-17071" />
                    <button type="button" className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                const input = document.getElementById('customCountry');
                if (input.value) {
                  setFormData({
                    ...formData,
                    countries: [...formData.countries.filter(c => c !== 'Other'), input.value]
                  });
                  input.value = '';
                }
              }} data-id="element-17072">
                      Add
                    </button>
                  </div>
                </div>}
              {/* Selected countries */}
              <div className="sm:col-span-6" data-id="element-17073">
                <div className="mt-1" data-id="element-17074">
                  {formData.countries.length === 0 ? <p className="text-sm text-gray-500" data-id="element-17075">
                      No countries selected. Please add at least one country
                      that uses this currency.
                    </p> : <div className="flex flex-wrap gap-2" data-id="element-17076">
                      {formData.countries.map(country => <div key={country} className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-17077">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-17078" />
                          {country}
                          <button type="button" className="ml-1 text-indigo-600 hover:text-indigo-900 focus:outline-none" onClick={() => handleRemoveCountry(country)} data-id="element-17079">
                            &times;
                          </button>
                        </div>)}
                    </div>}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200" data-id="element-17080">
              <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17081">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17082">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-17083" />
                {isCreating ? 'Create Currency' : 'Update Currency'}
              </button>
            </div>
          </form>}

        {/* Relationships Tab */}
        {activeTab === 'relationships' && <div className="py-4" data-id="element-17084">
            <div className="text-center py-12" data-id="element-17085">
              <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17086" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17087">
                No relationships
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-17088">
                Currency relationships will be displayed here.
              </p>
            </div>
          </div>}

        {/* History Tab */}
        {activeTab === 'history' && <div className="py-4" data-id="element-17089">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-17090">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-17091">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-17092">
                  Currency History
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-17093">
                  {history.length} events
                </p>
              </div>

              <div className="px-6 py-6" data-id="element-17094">
                {history.length === 0 ? <div className="text-center py-12" data-id="element-17095">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17096" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17097">
                      No history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-17098">
                      No changes have been recorded for this currency yet.
                    </p>
                  </div> : <div className="flow-root" data-id="element-17099">
                    <ul className="-mb-8" data-id="element-17100">
                      {history.map((event, eventIdx) => <li key={event.id} data-id="element-17101">
                          <div className="relative pb-8" data-id="element-17102">
                            {eventIdx !== history.length - 1 && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-17103" />}
                            <div className="relative flex items-start space-x-3" data-id="element-17104">
                              <div data-id="element-17105">
                                <div className="relative px-1" data-id="element-17106">
                                  <div className="h-10 w-10 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center" data-id="element-17107">
                                    {getEventIcon(event.eventType)}
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-17108">
                                <div data-id="element-17109">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-17110">
                                    {event.eventDescription}
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-17111">
                                    {formatTimestamp(event.timestamp)}
                                  </p>
                                </div>
                                {event.changes && event.changes.length > 0 && <div className="mt-2 text-sm text-gray-700" data-id="element-17112">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-17113">
                                      {event.changes.map((change, idx) => <div key={idx} className="flex items-center text-xs" data-id="element-17114">
                                          <span className="font-medium text-gray-700" data-id="element-17115">
                                            {change.field}:
                                          </span>
                                          <span className="ml-2 text-gray-500 line-through" data-id="element-17116">
                                            {change.oldValue}
                                          </span>
                                          <span className="mx-2 text-gray-400" data-id="element-17117">
                                            →
                                          </span>
                                          <span className="text-gray-900" data-id="element-17118">
                                            {change.newValue}
                                          </span>
                                        </div>)}
                                    </div>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}